/** Why a set of terms is not offered at this order size, or null when it is. */
export const termsOptionSizeShortfall = (option, size) => {
    const minValue = Number(option.minOrderValue) || 0;
    const minQty = Number(option.minOrderQty) || 0;
    const value = Number(size.value) || 0;
    const quantity = Number(size.quantity) || 0;
    if (minValue > 0 && value < minValue)
        return { kind: 'value', needed: minValue, have: value };
    if (minQty > 0 && quantity < minQty)
        return { kind: 'quantity', needed: minQty, have: quantity };
    return null;
};
/**
 * The options an order of this size qualifies for. A set with a minimum the order does not
 * reach is left out, so a single survivor applies on its own exactly as a single option would.
 */
export const termsOptionsForOrderSize = (options, size) => options.filter((o) => termsOptionSizeShortfall(o, size) === null);
export const STANDARD_TERMS_OPTION_ID = 'standard';
export const STANDARD_TERMS_OPTION_NAME = 'Standard';
const PERCENT_TOLERANCE = 0.01;
const normalise = (value) => (value ?? '').trim().toLowerCase();
/** Two sets of terms are the same deal when every row matches, in order. */
export const isSameTermsTemplate = (a, b) => {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length)
        return false;
    return left.every((row, idx) => {
        const other = right[idx];
        return (row.type === other.type &&
            row.anchor === other.anchor &&
            Number(row.percentage) === Number(other.percentage) &&
            (row.offsetDays ?? 0) === (other.offsetDays ?? 0) &&
            row.label.trim() === other.label.trim());
    });
};
/** Why a set of terms cannot be used, or null when it can. Percentages must sum to 100. */
export const termsTemplateError = (template) => {
    if (!template.length)
        return 'Payment terms need at least one tranche.';
    const sum = template.reduce((s, t) => s + (Number(t.percentage) || 0), 0);
    if (Math.abs(sum - 100) > PERCENT_TOLERANCE) {
        return `Tranche percentages must sum to 100 (got ${sum}).`;
    }
    return null;
};
/** Every set of terms the vendor offers, the default first. */
export const listVendorTermsOptions = (config) => {
    if (!config)
        return [];
    let options;
    if (config.options?.length) {
        options = config.options.filter((o) => o.tranches?.length);
    }
    else {
        options = [];
        const base = config.default?.length ? config.default : null;
        if (base) {
            options.push({
                id: STANDARD_TERMS_OPTION_ID,
                name: STANDARD_TERMS_OPTION_NAME,
                isDefault: true,
                tranches: base,
            });
        }
        const overrides = (config.byDestination ?? []).filter((entry) => entry.tranches?.length && !(base && isSameTermsTemplate(base, entry.tranches)));
        for (const entry of overrides) {
            options.push({
                id: `country:${normalise(entry.country)}`,
                name: entry.country.trim(),
                country: entry.country.trim(),
                tranches: entry.tranches,
            });
        }
    }
    return [...options].sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
};
/**
 * The options offered on an order bound for `country`: untagged ones, plus those tagged with
 * that country. An order with no country is offered everything, tagged options included -
 * hiding them could hide the right one.
 */
export const termsOptionsForCountry = (options, country) => {
    const wanted = normalise(country);
    if (!wanted)
        return options;
    return options.filter((o) => !normalise(o.country) || normalise(o.country) === wanted);
};
/** Why a vendor's options cannot be saved, or null when they can. */
export const vendorTermsOptionsError = (options) => {
    const list = options ?? [];
    const names = new Set();
    const ids = new Set();
    let defaults = 0;
    for (const option of list) {
        const name = option.name?.trim();
        if (!name)
            return 'Every set of payment terms needs a name.';
        if (names.has(normalise(name))) {
            return `Two sets of payment terms are both named "${name}".`;
        }
        names.add(normalise(name));
        if (!option.id || ids.has(option.id)) {
            return `Payment terms "${name}" need an id of their own.`;
        }
        ids.add(option.id);
        const rowsError = termsTemplateError(option.tranches ?? []);
        if (rowsError)
            return `${name}: ${rowsError}`;
        if (option.isDefault) {
            defaults += 1;
            // A default hidden from other countries would be a default nobody is offered.
            if (normalise(option.country)) {
                return `"${name}" is tagged to a country, so it cannot be the default.`;
            }
        }
    }
    if (defaults > 1)
        return 'Only one set of payment terms can be the default.';
    return null;
};
//# sourceMappingURL=vendorTermsOptions.js.map